import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { applications, insights, pipeline, platform, products, science } from "../data";
import { ArrowIcon } from "../components/Icons";
import PlatformObservatory from "../components/PlatformObservatory";
import Seo from "../components/Seo";
import { assetUrl } from "../asset-url";

const productFilters = ["All", "Precision Oncology", "Industrial Resilience", "Biodefense and CBRN Threat", "Longevity"];

const homepageScienceCopy = {
  "3d-million-base-dna-synthesis": {
    short: "Structured, traceable systems for constructing very large DNA architectures.",
  },
  "umbert-genome-assembly": {
    title: "UMBERT Genome Integration",
    short: "Programmable gene-cassette capture and rearrangement system for dynamically reconfiguring functions",
  },
  "artificial-delivery-vehicle-protein": {
    short: "Engineered polyprotein for efficient, cell-independent intracellular delivery with minimal immunogenicity",
  },
  "direct-molecular-counting": {
    short: "Precision measurement systems for direct, quality-aware molecular signals.",
  },
};

const homepagePlatformCopy = {
  "programmable-life-surrogate": {
    title: "Programmable Surrogate-Host System",
    short: "Selection, Engineering, and Incubation of Programmable Life",
  },
};

const homepageApplicationCopy = {
  "industrial-resilience": {
    short: "Biological systems and precision measurement for resilient production environments.",
  },
  "biodefense-cbrn-threat": {
    points: ["Detection capabilities", "Preparedness workflows", "Controlled access"],
  },
};

const homepageProductCopy = {
  lungdefend: "Lung cancer early detection",
  bremony: "Breast cancer early detection",
  prosglad: "Prostate cancer early detection",
  detedx: "Personalized TP53 monitoring",
  "bioweave-buildup-film": "Biofabricated high-performance interlayer insulating film",
  "opticull-x": "Selective attenuation of Y-bearing sperm and improvement of female offspring yield",
  rynvetha: "Advanced radioprotective solution to mitigate high-dose radiation-induced burns and tissue damage.",
  ulmyxen: "Advanced radioprotective solution to reduce systemic toxicity associated with high-dose radiation exposure.",
  "cololite-plus": "Precision nutrition solution to extend healthspan and improve quality of life",
};

const homepageProductTitles = {
  "bioweave-buildup-film": "BioWeave build-up Film",
};

const homepageProductOrder = [
  "lungdefend",
  "bremony",
  "prosglad",
  "detedx",
  "bioweave-buildup-film",
  "opticull-x",
  "rynvetha",
  "ulmyxen",
  "cololite-plus",
];
const homepageProductRank = new Map(homepageProductOrder.map((slug, index) => [slug, index]));

const homepagePipelineCopy = {
  tolerivex: "Programmable immune reset for durable autoimmune remission.",
  "methuselon-969": "Whole-body cellular reprogramming designed for 10× lifespan extension",
};

const homepageScience = science.map((item) => ({ ...item, ...homepageScienceCopy[item.slug] }));
const homepagePlatform = platform.map((item) => ({ ...item, ...homepagePlatformCopy[item.slug] }));
const homepageApplications = applications.map((item) => ({ ...item, ...homepageApplicationCopy[item.slug] }));
const homepageProducts = products.map((item) => ({
  ...item,
  title: homepageProductTitles[item.slug] ?? item.title,
  short: homepageProductCopy[item.slug] ?? item.short,
})).sort(
  (first, second) => (
    (homepageProductRank.get(first.slug) ?? Number.MAX_SAFE_INTEGER)
    - (homepageProductRank.get(second.slug) ?? Number.MAX_SAFE_INTEGER)
  ),
);
const homepagePipeline = pipeline.map((item) => ({ ...item, short: homepagePipelineCopy[item.slug] ?? item.short }));

function handleTabKeyDown(event, index, itemCount, setActiveIndex) {
  const keyDirections = {
    ArrowDown: 1,
    ArrowRight: 1,
    ArrowUp: -1,
    ArrowLeft: -1,
  };

  if (!(event.key in keyDirections) && event.key !== "Home" && event.key !== "End") return;

  event.preventDefault();
  let nextIndex = index;
  if (event.key === "Home") nextIndex = 0;
  else if (event.key === "End") nextIndex = itemCount - 1;
  else nextIndex = (index + keyDirections[event.key] + itemCount) % itemCount;

  setActiveIndex(nextIndex);
  event.currentTarget.parentElement.querySelectorAll('[role="tab"]')[nextIndex]?.focus();
}

export default function HomePage() {
  const [productFilter, setProductFilter] = useState("All");
  const [activeApplicationIndex, setActiveApplicationIndex] = useState(0);
  const [activePipelineIndex, setActivePipelineIndex] = useState(0);
  const visibleProducts = useMemo(
    () => (productFilter === "All" ? homepageProducts : homepageProducts.filter((product) => product.area === productFilter)),
    [productFilter],
  );
  const activeApplication = homepageApplications[activeApplicationIndex];
  const activeProgram = homepagePipeline[activePipelineIndex];

  return (
    <>
      <Seo />
      <section className="hero section" aria-labelledby="home-title">
        <div className="hero-copy">
          <h1 id="home-title">
            <span>Programmable Life.</span>
            <span>Precision Science</span>
          </h1>
          <p className="hero-lead">
            <span className="hero-lead-full">
              Medein connects advanced biology, AI-driven design, precision assays, and translational execution
              to build scalable life-science platforms and product systems.
            </span>
            <span className="hero-lead-mobile">
              Medein connects biology, AI-driven design, precision assays, and translational execution.
            </span>
          </p>
          <div className="button-row">
            <Link className="button button-primary" to="/platform">
              Explore Our Platform <ArrowIcon />
            </Link>
            <Link className="button button-secondary hero-partner-button" to="/partnership">
              Partner With Us <ArrowIcon />
            </Link>
          </div>
          <Link className="hero-investor-link" to="/investors">
            Investor overview <ArrowIcon />
          </Link>
        </div>
        <PlatformObservatory modules={homepagePlatform} />
      </section>

      <section className="scope-rail" aria-label="Medein platform scope">
        {[
          ["01", "Science technologies"],
          ["02", "Platform modules"],
          ["03", "Application areas"],
          ["04", "Pipeline programs"],
        ].map(([number, label]) => (
          <div key={label}>
            <strong>{number}</strong>
            <span>{label}</span>
          </div>
        ))}
      </section>

      <section id="science" className="section science-section">
        <div className="section-heading split-heading">
          <div>
            <p className="section-label">Science technologies</p>
            <h2>Engineering biology<br />at a new scale.</h2>
          </div>
          <p>
            Four foundational technology systems connect design, build, assembly, delivery, and measurement
            through one traceable science layer.
          </p>
        </div>
        <div className="science-editorial">
          <Link className="science-image-card" to="/science">
            <img
              src={assetUrl("/assets/optimized/microfluidic.webp")}
              alt="Microfluidic assay system in a high-key laboratory setting"
              loading="lazy"
              decoding="async"
            />
            <span>Explore the science layer <ArrowIcon /></span>
          </Link>
          <div className="science-list">
            {homepageScience.map((item, index) => (
              <Link className="science-item" key={item.slug} to={`/science/${item.slug}`}>
                <img src={item.icon} alt="" loading="lazy" decoding="async" />
                <span className="item-number">{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.short}</p>
                </div>
                <ArrowIcon />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section platform-section">
        <div className="platform-intro">
          <p className="section-label">Platform architecture</p>
          <h2>One platform.<br />Four interoperable <span>modules.</span></h2>
          <p>
            A governed design architecture that connects biological context, computation, simulation, and
            precision measurement.
          </p>
          <Link className="text-link" to="/platform">Explore the platform <ArrowIcon /></Link>
        </div>
        <div className="platform-map">
          <div className="platform-core">
            <img src={assetUrl("/assets/science_icon_svg_019_signal_bars.svg")} alt="" loading="lazy" decoding="async" />
            <strong>Medein</strong>
            <span>Design core</span>
          </div>
          {homepagePlatform.map((item, index) => (
            <Link
              className={`platform-module module-${index + 1}`}
              key={item.slug}
              to={`/platform/${item.slug}`}
            >
              <img src={item.icon} alt="" loading="lazy" decoding="async" />
              <span>{String(index + 1).padStart(2, "0")}</span>
              <h3>{item.title}</h3>
              <p>{item.short}</p>
            </Link>
          ))}
          <svg className="platform-connectors" viewBox="0 0 1000 680" aria-hidden="true">
            <path d="M500 340 C410 340 420 190 285 190" />
            <path d="M500 340 C590 340 580 190 715 190" />
            <path d="M500 340 C410 340 420 515 285 515" />
            <path d="M500 340 C590 340 580 515 715 515" />
          </svg>
        </div>
        <div className="platform-media">
          <img
            src={assetUrl("/assets/optimized/platform-assay.webp")}
            alt="Precision assay microfluidic detail"
            loading="lazy"
            decoding="async"
          />
          <p>Measurement closes the loop between design hypotheses and governed evidence.</p>
        </div>
      </section>

      <section id="applications" className="section applications-section">
        <div className="section-heading split-heading applications-heading">
          <div>
            <p className="section-label">Applications</p>
            <h2>From platform capability<br />to <span>real-world application.</span></h2>
          </div>
          <p>
            Shared technology systems are focused through distinct research, product, and evidence pathways.
          </p>
        </div>
        <div className="application-showcase">
          <article
            className="application-feature"
            id="application-feature-panel"
            role="tabpanel"
            aria-labelledby={`application-tab-${activeApplicationIndex}`}
            aria-live="polite"
          >
            <Link className="application-feature-media" to={`/applications/${activeApplication.slug}`}>
              <img
                src={activeApplication.image}
                alt={`${activeApplication.title} research visualization`}
                loading="lazy"
                decoding="async"
              />
              <span className="application-feature-index">
                {String(activeApplicationIndex + 1).padStart(2, "0")} / {String(applications.length).padStart(2, "0")}
              </span>
            </Link>
            <div className="application-feature-copy">
              <p className="application-feature-label">Selected application</p>
              <h3>{activeApplication.title}</h3>
              <p>{activeApplication.short}</p>
              <div className="application-points" aria-label={`${activeApplication.title} focus areas`}>
                {activeApplication.points.map((point) => <span key={point}>{point}</span>)}
              </div>
              <Link className="text-link" to={`/applications/${activeApplication.slug}`}>
                Explore application <ArrowIcon />
              </Link>
            </div>
          </article>
          <div className="application-selector" role="tablist" aria-label="Choose an application area">
            {homepageApplications.map((item, index) => (
              <button
                className={`application-tab ${activeApplicationIndex === index ? "is-active" : ""}`}
                key={item.slug}
                id={`application-tab-${index}`}
                type="button"
                role="tab"
                aria-selected={activeApplicationIndex === index}
                aria-controls="application-feature-panel"
                tabIndex={activeApplicationIndex === index ? 0 : -1}
                onClick={() => setActiveApplicationIndex(index)}
                onKeyDown={(event) => handleTabKeyDown(event, index, homepageApplications.length, setActiveApplicationIndex)}
              >
                <span className="application-tab-index">{String(index + 1).padStart(2, "0")}</span>
                <img src={item.icon} alt="" loading="lazy" decoding="async" />
                <span className="application-tab-copy">
                  <strong>{item.title}</strong>
                  <small>{item.short}</small>
                </span>
                <ArrowIcon />
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="product-section">
        <div className="section product-inner">
          <div className="section-heading split-heading">
            <div>
              <p className="section-label">Product line</p>
              <h2>A growing product system.</h2>
            </div>
            <Link className="button button-secondary" to="/product-line">View product line <ArrowIcon /></Link>
          </div>
          <div className="product-filters" role="group" aria-label="Filter products by application">
            {productFilters.map((filter) => (
              <button
                className={productFilter === filter ? "is-active" : ""}
                key={filter}
                onClick={() => setProductFilter(filter)}
                aria-pressed={productFilter === filter}
              >
                {filter}
              </button>
            ))}
          </div>
          <div className="product-list" aria-live="polite">
            {visibleProducts.map((product) => (
              <Link key={product.slug} to={`/product-line/${product.slug}`}>
                <img src={product.icon} alt="" loading="lazy" decoding="async" />
                <div>
                  <span>{product.area}</span>
                  <h3>{product.title}</h3>
                  <p>{product.short}</p>
                </div>
                <ArrowIcon />
              </Link>
            ))}
          </div>
          <p className="compliance-note">
            Research, product and program status varies by region, evidence level, and authorized use.
          </p>
        </div>
      </section>

      <section id="pipeline" className="section pipeline-section">
        <div className="pipeline-heading">
          <div>
            <p className="section-label">Pipeline</p>
            <h2>A pipeline built for<br />traceable progress.</h2>
          </div>
          <Link className="button button-secondary" to="/pipeline">Explore pipeline <ArrowIcon /></Link>
        </div>
        <div className="pipeline-workbench">
          <div className="pipeline-workbench-visual" aria-hidden="true">
            <img src={assetUrl("/assets/optimized/hero-data-wave.webp")} alt="" loading="lazy" decoding="async" />
          </div>
          <article
            className="pipeline-detail"
            id="pipeline-detail-panel"
            role="tabpanel"
            aria-labelledby={`pipeline-tab-${activePipelineIndex}`}
            aria-live="polite"
          >
            <div className="pipeline-detail-meta">
              <span>Program {String(activePipelineIndex + 1).padStart(2, "0")} / {String(homepagePipeline.length).padStart(2, "0")}</span>
              <span>{activeProgram.status}</span>
            </div>
            <img className="pipeline-detail-icon" src={activeProgram.icon} alt="" loading="lazy" decoding="async" />
            <p className="pipeline-detail-area">{activeProgram.area}</p>
            <h3>{activeProgram.title}</h3>
            <p className="pipeline-detail-summary">{activeProgram.short}</p>
            <div className="pipeline-detail-points" aria-label={`${activeProgram.title} program principles`}>
              {activeProgram.points.map((point) => <span key={point}>{point}</span>)}
            </div>
            <Link className="button button-light" to={`/pipeline/${activeProgram.slug}`}>
              View program <ArrowIcon />
            </Link>
          </article>
          <div className="pipeline-selector" role="tablist" aria-label="Choose a pipeline program">
            <p className="pipeline-selector-label">Research programs</p>
            {homepagePipeline.map((program, index) => (
              <button
                className={`pipeline-tab ${activePipelineIndex === index ? "is-active" : ""}`}
                key={program.slug}
                id={`pipeline-tab-${index}`}
                type="button"
                role="tab"
                aria-selected={activePipelineIndex === index}
                aria-controls="pipeline-detail-panel"
                tabIndex={activePipelineIndex === index ? 0 : -1}
                onClick={() => setActivePipelineIndex(index)}
                onKeyDown={(event) => handleTabKeyDown(event, index, homepagePipeline.length, setActivePipelineIndex)}
              >
                <span className="pipeline-tab-index">{String(index + 1).padStart(2, "0")}</span>
                <span className="pipeline-tab-copy">
                  <small>{program.area}</small>
                  <strong>{program.title}</strong>
                </span>
                <span className="pipeline-tab-status">{program.status}</span>
                <ArrowIcon />
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="evidence-band">
        <div>
          <p className="section-label">Evidence governance</p>
          <h2>Every public claim<br />connects to evidence.</h2>
        </div>
        {[
          ["science_icon_svg_017_sample_analysis.svg", "Evidence level", "The nature and strength of supporting material."],
          ["science_icon_svg_019_signal_bars.svg", "Last reviewed", "When content and evidence were scientifically reviewed."],
          ["science_icon_svg_018_shield_check.svg", "Access status", "How evidence is shared and who can access it."],
        ].map(([icon, title, text]) => (
          <div className="evidence-item" key={title}>
            <img src={assetUrl(`/assets/${icon}`)} alt="" loading="lazy" decoding="async" />
            <h3>{title}</h3>
            <p>{text}</p>
          </div>
        ))}
        <p className="evidence-status">Status is updated through scientific and regulatory review.</p>
      </section>

      <section className="section insights-section">
        <div className="section-heading split-heading">
          <div>
            <p className="section-label">Insights</p>
            <h2>Ideas, evidence<br /><span>and progress.</span></h2>
          </div>
          <p>Exploring the science, systems, and ideas advancing programmable life.</p>
        </div>
        <div className="insights-grid">
          {insights.map((insight, index) => (
            <Link className={`insight-card insight-${index + 1}`} key={insight.title} to="/insights">
              <img src={insight.image} alt="" loading="lazy" decoding="async" />
              <div>
                <span>{insight.category}</span>
                <h3>{insight.title}</h3>
                <p>{insight.summary}</p>
                <span className="text-link">Read insight <ArrowIcon /></span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="partnership-cta">
        <div>
          <h2>Build what comes next—together.</h2>
          <p>We partner across research, product development, validation, and translation.</p>
        </div>
        <div className="button-row">
          <Link className="button button-light" to="/partnership">Partner With Us <ArrowIcon /></Link>
          <Link className="button button-ghost-light" to="/contact">Contact Medein <ArrowIcon /></Link>
        </div>
        <div className="cta-signal" aria-hidden="true" />
      </section>
    </>
  );
}
